import { useCallback, useMemo } from 'react'

import { useLocalStorage } from '../useStorage'
import {
  DEFAULT_ONBOARDING_STORAGE_KEY,
  ONBOARDING_ENABLED,
} from './consts'
import type { OnboardingStep } from './types'
import { findActiveStep } from './utils'

/**
 * Persists step completion in localStorage and exposes the first eligible step.
 *
 * **Recipe**
 * 1. Put `elementId` on a DOM node (`id={...}`).
 * 2. Define `steps` (order = priority). Use `when` / `requiresSeenIds` for gates.
 * 3. Render `OnboardingAnchor` with `activeStep`, `onAcknowledge`, and optional
 *    `measureKey` when React state changes without reliable layout signals.
 */
export function useOnboarding({
  steps,
  storageKey = DEFAULT_ONBOARDING_STORAGE_KEY,
}: {
  steps: OnboardingStep[]
  storageKey?: string
}) {
  const [seenMap, setSeenMap] = useLocalStorage<Record<string, boolean>>(
    storageKey,
    {}
  )

  const activeStep = useMemo(
    () => (ONBOARDING_ENABLED ? findActiveStep(steps, seenMap) : null),
    [steps, seenMap]
  )

  const acknowledge = useCallback(() => {
    setSeenMap((prev) => {
      const id = activeStep?.elementId
      if (!id) return prev
      return { ...prev, [id]: true }
    })
  }, [activeStep?.elementId, setSeenMap])

  return { activeStep, acknowledge, seenMap }
}

export { OnboardingAnchor } from './components/OnboardingAnchor'

export {
  DefaultOnboardingTooltip,
  createDefaultOnboardingStep,
} from './components/DefaultOnboardingTooltip'

export type {
  OnboardingAnchorProps,
  OnboardingStep,
  OnboardingTooltipRenderProps,
} from './types'

export {
  assistantOnboarding,
  DEFAULT_ONBOARDING_STORAGE_KEY,
  ONBOARDING_ELEMENT_IDS,
  ONBOARDING_ENABLED,
  ONBOARDING_STORAGE_KEYS,
  onboardingConfigs,
} from './consts'
